import React, { useCallback, useEffect, useRef, useState } from "react";
import { MDBContainer } from "mdb-react-ui-kit";
import api from "../services/api";
import { RetryImage, PLACEHOLDER_IMG } from "./ViewProductsPage";

import "./ViewProductsPage.css";
import "./DepopOpportunitiesPage.css";

// Owner-only. All pricing, sold-exclusion, currency and difference logic
// lives in the backend (GET /api/depop/opportunities) — this page only
// displays what the API returns. No currency conversion anywhere.

const PAGE_SIZE = 20;
const SEARCH_DEBOUNCE_MS = 400;

// Keys + default direction mirror the backend's SORT_OPTIONS.
const SORT_OPTIONS = [
  { key: "predicted_price", label: "Predicted Price", order: "DESC" },
  { key: "price_difference", label: "Price Difference", order: "DESC" },
  { key: "similarity_weighted_price", label: "Similarity Weighted Price", order: "DESC" },
  { key: "avg_time_to_sale", label: "Average Time To Sale", order: "ASC" },
  { key: "sold_comps_count", label: "Number of Sold Comps", order: "DESC" },
  { key: "our_price", label: "Our Price", order: "DESC" },
  { key: "researched_at", label: "Research Date", order: "DESC" },
];

const COMPARISON_REASONS = {
  currency_mismatch: "currency mismatch",
  depop_currency_unknown: "Depop currency unknown",
  our_currency_unknown: "our currency unknown",
  price_missing: "price missing",
};

// Amount in its own currency, e.g. "£55.00 GBP" / "€25.00 EUR".
// Without a known currency, no symbol is shown.
const formatMoney = (amount, currency, { signed = false } = {}) => {
  if (amount === null || amount === undefined || !Number.isFinite(Number(amount))) {
    return "—";
  }
  const value = Number(amount);
  if (!currency) {
    return `${value.toFixed(2)} (currency unknown)`;
  }
  try {
    const formatted = new Intl.NumberFormat("en-GB", {
      style: "currency",
      currency,
      currencyDisplay: "narrowSymbol",
      signDisplay: signed ? "exceptZero" : "auto",
    }).format(value);
    return `${formatted} ${currency}`;
  } catch {
    return `${signed && value > 0 ? "+" : ""}${value.toFixed(2)} ${currency}`;
  }
};

const formatDate = (value) => {
  if (!value) return "—";
  const d = new Date(value);
  return Number.isNaN(d.getTime())
    ? "—"
    : d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
};

const formatDays = (value) =>
  value === null || value === undefined ? "—" : `${value} day${Number(value) === 1 ? "" : "s"}`;

// "USED_EXCELLENT" / "used_excellent" -> "Used Excellent"
const humanize = (value) =>
  value
    ? String(value)
        .replace(/_/g, " ")
        .toLowerCase()
        .replace(/\b\w/g, (c) => c.toUpperCase())
    : null;

const getErrorMessage = (err, fallback) => {
  if (err?.response?.status === 403) {
    return "Access denied. Depop Opportunities is available to the owner only.";
  }
  return err?.response?.data?.message || err?.message || fallback;
};

const DetailItem = ({ label, value }) => (
  <div className="detail-item">
    <span className="detail-label">{label}</span>
    <span className="detail-value">{value ?? "—"}</span>
  </div>
);

const PriceComparison = ({ opportunity }) => {
  if (!opportunity) return null;
  if (opportunity.comparable) {
    const diff = opportunity.potentialDifference;
    const tone = diff > 0 ? "positive" : diff < 0 ? "negative" : "neutral";
    return (
      <div className={`depop-diff depop-diff--${tone}`}>
        <span className="detail-label">Potential Difference</span>
        <span className="depop-diff-value">
          {formatMoney(diff, opportunity.currency, { signed: true })}
        </span>
        <small>Depop predicted price − our price</small>
      </div>
    );
  }
  return (
    <div className="depop-diff depop-diff--unavailable">
      <span className="detail-label">Potential Difference</span>
      <span>
        Price comparison unavailable:{" "}
        {COMPARISON_REASONS[opportunity.reason] || "not comparable"}
      </span>
    </div>
  );
};

const PricingDetails = ({ pricing, full = false }) => (
  <div className="details-grid">
    <DetailItem label="Predicted" value={formatMoney(pricing.predictedPrice, pricing.currency)} />
    <DetailItem label="Lower Bound" value={formatMoney(pricing.lowerBound, pricing.currency)} />
    <DetailItem label="Upper Bound" value={formatMoney(pricing.upperBound, pricing.currency)} />
    <DetailItem label="Median Sold" value={formatMoney(pricing.medianSoldPrice, pricing.currency)} />
    <DetailItem label="Average Sold" value={formatMoney(pricing.avgSoldPrice, pricing.currency)} />
    {full && (
      <>
        <DetailItem label="Minimum Sold" value={formatMoney(pricing.minSoldPrice, pricing.currency)} />
        <DetailItem label="Maximum Sold" value={formatMoney(pricing.maxSoldPrice, pricing.currency)} />
      </>
    )}
    <DetailItem
      label="Similarity Weighted"
      value={formatMoney(pricing.similarityWeightedPrice, pricing.currency)}
    />
    <DetailItem label="Sold Comps" value={pricing.soldCompsCount} />
    <DetailItem label="Avg Time To Sale" value={formatDays(pricing.avgTimeToSaleDays)} />
  </div>
);

const ResearchDetails = ({ research }) => (
  <div className="details-grid">
    <DetailItem label="Brand" value={research.brand || "—"} />
    <DetailItem label="Condition" value={research.condition || "—"} />
    <DetailItem label="Country" value={research.country || "—"} />
    <DetailItem label="Researched" value={formatDate(research.researchedAt)} />
  </div>
);

const StatusBadge = ({ status }) =>
  status ? (
    <span className={`status-badge status-${status.toLowerCase().replace(/\s+/g, "_")}`}>
      {humanize(status)}
    </span>
  ) : null;

// ================= DETAIL MODAL =================
const OpportunityDetailModal = ({ productId, onClose }) => {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    api
      .get(`/api/depop/opportunities/${productId}`)
      .then((res) => {
        if (!cancelled) setDetail(res.data?.data || null);
      })
      .catch((err) => {
        if (!cancelled) setError(getErrorMessage(err, "Failed to load Depop details."));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [productId, reloadKey]);

  useEffect(() => {
    const onKeyDown = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [onClose]);

  const product = detail?.product;
  const pricing = detail?.depopPricing;
  const research = detail?.depopResearch;
  const marketplaces = detail?.marketplaces;
  const comparables = detail?.comparables || [];

  return (
    <div className="reason-modal-overlay" onClick={onClose}>
      <div
        className="reason-modal-content depop-detail-modal"
        role="dialog"
        aria-modal="true"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header">
          <div className="modal-title-section">
            <h2 className="modal-product-title">
              {product?.title || "Depop Opportunity"}
            </h2>
            <h3 className="modal-analysis-type">Depop pricing details</h3>
          </div>
        </div>

        <div className="modal-body">
          {loading ? (
            <div className="py-5 text-center">
              <h4>Loading Depop details...</h4>
            </div>
          ) : error ? (
            <div className="alert alert-danger" role="alert">
              {error}
              <button
                type="button"
                className="btn btn-sm btn-outline-danger ms-2"
                onClick={() => setReloadKey((k) => k + 1)}
              >
                Retry
              </button>
            </div>
          ) : detail ? (
            <>
              <section className="depop-section">
                <h4 className="depop-section-title">Our Product</h4>
                <div className="depop-detail-product">
                  <RetryImage
                    src={product.images?.[0] || PLACEHOLDER_IMG}
                    alt={product.title}
                    className="product-image"
                  />
                  <div className="details-grid depop-detail-product-grid">
                    <DetailItem label="SKU" value={product.sku || "—"} />
                    <DetailItem label="Our Price" value={formatMoney(product.price, product.currency)} />
                    <DetailItem label="Brand" value={product.brand || "Not available"} />
                    <DetailItem label="Condition" value={humanize(product.condition) || "Not available"} />
                    <DetailItem label="Quantity" value={product.quantity ?? "—"} />
                    <DetailItem label="Status" value={<StatusBadge status={product.status} />} />
                  </div>
                </div>

                <div className="depop-platforms">
                  {(marketplaces?.platforms || []).map((pl) => (
                    <span key={`${pl.platform}-${pl.platformProductId}`} className="depop-platform-badge">
                      {humanize(pl.platform)}: {humanize(pl.status) || "Unknown"}
                    </span>
                  ))}
                  {marketplaces?.depop ? (
                    <span className="depop-platform-badge">
                      Depop listing: {humanize(marketplaces.depop.status) || "Unknown"}
                    </span>
                  ) : (
                    <span className="depop-platform-badge">Not listed on Depop</span>
                  )}
                  {!marketplaces?.platforms?.length && !marketplaces?.depop && (
                    <span className="depop-platform-badge">No marketplace listings</span>
                  )}
                </div>
              </section>

              <section className="depop-section">
                <h4 className="depop-section-title">Depop Research</h4>
                <ResearchDetails research={research} />
              </section>

              <section className="depop-section">
                <h4 className="depop-section-title">Depop Pricing</h4>
                <PricingDetails pricing={pricing} full />
                <PriceComparison opportunity={detail.opportunity} />
              </section>

              <section className="depop-section">
                <h4 className="depop-section-title">
                  Comparable Sold Items ({comparables.length})
                </h4>
                {comparables.length === 0 ? (
                  <div className="empty-state">
                    <p>No comparable sold items.</p>
                  </div>
                ) : (
                  <div className="depop-comps">
                    {comparables.map((comp) => (
                      <div key={comp.id} className="depop-comp">
                        <RetryImage
                          src={comp.imageUrl || PLACEHOLDER_IMG}
                          alt={comp.description || "Comparable item"}
                          className="depop-comp-image"
                        />
                        <div className="depop-comp-info">
                          <div className="depop-comp-brand">
                            {comp.brand || "Brand not recorded"}
                            {comp.brand && !comp.brandMatchesResearch && research?.brand && (
                              <span className="depop-brand-diff">
                                differs from research brand ({research.brand})
                              </span>
                            )}
                          </div>
                          <p className="depop-comp-description">
                            {comp.title || comp.description || "No description"}
                          </p>
                          <div className="product-badges">
                            <span className="product-badge">
                              Sold: {formatMoney(comp.soldPrice, comp.soldCurrency)}
                            </span>
                            <span className="product-badge">
                              Similarity:{" "}
                              {comp.similarityScore === null
                                ? "—"
                                : comp.similarityScore.toFixed(3)}
                            </span>
                            <span className="product-badge">
                              Condition: {humanize(comp.condition) || "—"}
                            </span>
                            <span className="product-badge">
                              Time to sale: {formatDays(comp.timeToSaleDays)}
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </section>
            </>
          ) : null}
        </div>

        <div className="modal-footer">
          <button type="button" className="modal-btn-close" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

// ================= PAGE =================
const DepopOpportunitiesPage = () => {
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [brand, setBrand] = useState("");
  const [condition, setCondition] = useState("");
  const [sortBy, setSortBy] = useState(SORT_OPTIONS[0].key);
  const [sortOrder, setSortOrder] = useState(SORT_OPTIONS[0].order);
  const [offset, setOffset] = useState(0);

  const [items, setItems] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [filterOptions, setFilterOptions] = useState({ brands: [], conditions: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedProductId, setSelectedProductId] = useState(null);

  const requestIdRef = useRef(0);

  // Debounced search -> backend; resets to the first page
  useEffect(() => {
    const next = searchInput.trim();
    if (next === search) return undefined;
    const t = setTimeout(() => {
      setSearch(next);
      setOffset(0);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [searchInput, search]);

  const fetchOpportunities = useCallback(async () => {
    const requestId = ++requestIdRef.current;
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.get("/api/depop/opportunities", {
        params: {
          limit: PAGE_SIZE,
          offset,
          sortBy,
          sortOrder,
          ...(search ? { search } : {}),
          ...(brand ? { brand } : {}),
          ...(condition ? { condition } : {}),
        },
      });
      if (requestId !== requestIdRef.current) return; // stale response
      setItems(data?.data?.items || []);
      setPagination(data?.data?.pagination || null);
      setFilterOptions({
        brands: data?.data?.filters?.brands || [],
        conditions: data?.data?.filters?.conditions || [],
      });
    } catch (err) {
      if (requestId !== requestIdRef.current) return;
      console.error("Depop opportunities error:", err);
      setError(getErrorMessage(err, "Failed to load Depop opportunities. Please try again."));
      setItems([]);
      setPagination(null);
    } finally {
      if (requestId === requestIdRef.current) setLoading(false);
    }
  }, [offset, sortBy, sortOrder, search, brand, condition]);

  useEffect(() => {
    fetchOpportunities();
  }, [fetchOpportunities]);

  const handleSortByChange = (key) => {
    const option = SORT_OPTIONS.find((o) => o.key === key);
    setSortBy(key);
    setSortOrder(option?.order || "DESC");
    setOffset(0);
  };

  const closeDetail = useCallback(() => setSelectedProductId(null), []);

  const total = pagination?.totalItems || 0;
  const showingFrom = total === 0 ? 0 : offset + 1;
  const showingTo = offset + items.length;
  const hasPrev = offset > 0;
  const hasNext = !!pagination?.hasMore;

  return (
    <MDBContainer fluid className="view-products-container depop-opportunities-page">
      <h2 className="mb-1">Depop Opportunities</h2>
      <p className="depop-intro">
        Products with Depop pricing research that are not currently sold.
      </p>

      <div className="filters-section">
        <div className="filters-grid">
          <div className="filter-item">
            <label htmlFor="depop-search">Search</label>
            <input
              id="depop-search"
              type="text"
              className="filter-input"
              placeholder="Search products..."
              maxLength={100}
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
            />
          </div>

          <div className="filter-item">
            <label htmlFor="depop-brand">Brand (Depop research)</label>
            <select
              id="depop-brand"
              className="filter-select"
              value={brand}
              onChange={(e) => {
                setBrand(e.target.value);
                setOffset(0);
              }}
            >
              <option value="">All Brands</option>
              {filterOptions.brands.map((b) => (
                <option key={b.value} value={b.value}>
                  {b.value} ({b.count})
                </option>
              ))}
            </select>
          </div>

          <div className="filter-item">
            <label htmlFor="depop-condition">Condition (Depop research)</label>
            <select
              id="depop-condition"
              className="filter-select"
              value={condition}
              onChange={(e) => {
                setCondition(e.target.value);
                setOffset(0);
              }}
            >
              <option value="">All Conditions</option>
              {filterOptions.conditions.map((c) => (
                <option key={c.value} value={c.value}>
                  {c.value} ({c.count})
                </option>
              ))}
            </select>
          </div>

          <div className="filter-item">
            <label htmlFor="depop-sort">Sort</label>
            <div className="depop-sort-controls">
              <select
                id="depop-sort"
                className="filter-select"
                value={sortBy}
                onChange={(e) => handleSortByChange(e.target.value)}
              >
                {SORT_OPTIONS.map((o) => (
                  <option key={o.key} value={o.key}>
                    {o.label}
                  </option>
                ))}
              </select>
              <select
                aria-label="Sort direction"
                className="filter-select"
                value={sortOrder}
                onChange={(e) => {
                  setSortOrder(e.target.value);
                  setOffset(0);
                }}
              >
                <option value="DESC">Descending</option>
                <option value="ASC">Ascending</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {error && (
        <div className="alert alert-danger" role="alert">
          {error}
          <button
            type="button"
            className="btn btn-sm btn-outline-danger ms-2"
            onClick={fetchOpportunities}
          >
            Retry
          </button>
        </div>
      )}

      {loading ? (
        <div className="py-5 text-center">
          <h4>Loading Depop opportunities...</h4>
        </div>
      ) : (
        <>
          {!error && (
            <div className="pagination-info depop-showing">
              Showing {showingFrom}–{showingTo} of {total}
            </div>
          )}

          <div className="product-list">
            {items.length === 0 ? (
              !error && (
                <div className="empty-state">
                  <i className="fas fa-box-open"></i>
                  <p>No Depop opportunities found.</p>
                </div>
              )
            ) : (
              items.map(({ product, depopResearch, depopPricing, opportunity }) => (
                <div key={product.id} className="product-card">
                  <div className="product-main">
                    <div className="product-main-content">
                      <RetryImage
                        key={product.primaryImage || "placeholder"}
                        src={product.primaryImage || PLACEHOLDER_IMG}
                        alt={product.title}
                        className="product-image"
                      />

                      <div className="product-info">
                        <h5 className="product-title">{product.title}</h5>
                        <div className="product-badges">
                          <span className="product-badge">
                            Our Price: {formatMoney(product.price, product.currency)}
                          </span>
                          <span className="product-badge">SKU: {product.sku || "—"}</span>
                          {product.condition && (
                            <span className="product-badge">
                              Condition: {humanize(product.condition)}
                            </span>
                          )}
                          {product.brand && (
                            <span className="product-badge">Brand: {product.brand}</span>
                          )}
                          <StatusBadge status={product.status} />
                        </div>

                        <div className="depop-card-sections">
                          <section className="depop-section">
                            <h6 className="depop-section-title">Depop Research</h6>
                            <ResearchDetails research={depopResearch} />
                          </section>
                          <section className="depop-section">
                            <h6 className="depop-section-title">Depop Pricing</h6>
                            <PricingDetails pricing={depopPricing} />
                          </section>
                          <section className="depop-section">
                            <h6 className="depop-section-title">Price Comparison</h6>
                            <div className="details-grid">
                              <DetailItem
                                label="Our Price"
                                value={formatMoney(product.price, product.currency)}
                              />
                              <DetailItem
                                label="Depop Predicted"
                                value={formatMoney(depopPricing.predictedPrice, depopPricing.currency)}
                              />
                            </div>
                            <PriceComparison opportunity={opportunity} />
                          </section>
                        </div>

                        <div className="product-actions">
                          <button
                            type="button"
                            className="btn btn-info btn-sm product-action-btn"
                            onClick={() => setSelectedProductId(product.id)}
                          >
                            View Details
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>

          {total > 0 && (
            <div className="pagination">
              <div className={`page-item ${hasPrev ? "" : "disabled"}`}>
                <button
                  type="button"
                  className="page-link"
                  disabled={!hasPrev}
                  onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
                >
                  Previous
                </button>
              </div>
              <div className={`page-item ${hasNext ? "" : "disabled"}`}>
                <button
                  type="button"
                  className="page-link"
                  disabled={!hasNext}
                  onClick={() => setOffset(offset + PAGE_SIZE)}
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {selectedProductId !== null && (
        <OpportunityDetailModal productId={selectedProductId} onClose={closeDetail} />
      )}
    </MDBContainer>
  );
};

export default DepopOpportunitiesPage;
